/* Linen Journal accessory add-ons (Elastic Band / Pen Loop) — Books Beyond Borders
 *
 * Same customer-facing idea as the Wool Journal add-ons (assets/wool-journal-addons.js):
 * an optional Elastic Band / Pen Loop, colour-previewed live on the product photo. This is
 * a SEPARATE file rather than a shared one because the two product lines' photos don't
 * share a photo template the way the whole Wool Journal line does — see the region-mapping
 * comment below for why that distinction matters here.
 */
(function () {
  'use strict';

  var root = document.getElementById('linen-journal-addons');
  if (!root) return;

  var pendingItems = null;

  /* ----------------------------------------------------------------------
   * Cart add-on logic (identical pattern to wool-journal-addons.js)
   * ------------------------------------------------------------------- */

  function getSelectedAddonItems() {
    var items = [];
    var productTitle = root.getAttribute('data-product-title');

    root.querySelectorAll('[data-addon]').forEach(function (addonEl) {
      var toggle = addonEl.querySelector('.checkbox-control input');
      if (!toggle || !toggle.checked) return;

      var checkedSwatch = addonEl.querySelector('input[type="radio"]:checked');
      var variantId = checkedSwatch
        ? checkedSwatch.closest('[data-variant-id]').getAttribute('data-variant-id')
        : addonEl.getAttribute('data-variant-id');

      if (variantId) {
        var item = { id: parseInt(variantId, 10), quantity: 1 };
        if (productTitle) {
          item.properties = { 'For': productTitle };
        }
        items.push(item);
      }
    });
    return items;
  }

  function refreshCartDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return Promise.resolve();
    return fetch('/?sections=cart-drawer')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (!data['cart-drawer']) return;
        var f = new DOMParser().parseFromString(data['cart-drawer'], 'text/html').querySelector('cart-drawer');
        if (f) drawer.innerHTML = f.innerHTML;
      });
  }

  function hookAfterCartAdd() {
    var origFetch = window.fetch;
    window.fetch = function (url, opts) {
      var urlStr = (typeof url === 'string') ? url : (url && url.url ? url.url : '');
      var isCartAdd = urlStr.indexOf('/cart/add.js') !== -1;
      var promise = origFetch.apply(this, arguments);

      if (isCartAdd && pendingItems && pendingItems.length) {
        var items = pendingItems;
        pendingItems = null;

        promise.then(function (response) {
          if (!response.ok) return;

          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: items })
          }).then(function () {
            return refreshCartDrawer();
          }).catch(function (err) {
            console.error('BBB linen-journal-addons: addon add failed', err);
          });
        }).catch(function () {});
      }

      return promise;
    };
  }

  function hookCart() {
    document.addEventListener('click', function (e) {
      var items = getSelectedAddonItems();
      if (!items.length) return;

      var btn = e.target.closest('button[type="submit"], [type="submit"]');
      if (!btn || btn.closest('cart-drawer')) return;

      var form = btn.closest('form[action*="/cart/add"], product-form');
      if (!form) return;

      pendingItems = items;
    }, false);
  }

  /* ----------------------------------------------------------------------
   * Live colour preview overlaid on the main product photo
   *
   * Unlike the Wool Journal line (one shared photo template, so a single set of
   * canvas-fraction REGIONS works for every product), the Linen Journal photos
   * frame the book at DIFFERENT sizes: measured directly from the actual photos,
   * the book fills about 84% of the reference photo's width but only about 75%
   * of the on-screen "bare" photo's width, and the two aren't simply the same
   * shot cropped differently either (the two per-product tests are separate
   * shoots). A region expressed as a fraction of the whole canvas — which is
   * how the Wool Journal script works — is therefore only correct for ONE of
   * the two photos at a time; stretching a mask built that way onto the other
   * photo drags the band and loop visibly off the book (this is exactly the
   * bug reported: the band shifted right of the spine and the loop floating
   * apart from the cover).
   *
   * The fix is to never reason in whole-canvas fractions. Every accessory's
   * position is measured once (see REGIONS_REL below) as a fraction of the
   * BOOK's own bounding box, which — once you divide out each photo's own
   * book size — is near-identical across every product and photo pair (verified
   * directly against all 6 current Linen Journal reference photos). At runtime,
   * findBookBBox() detects the book's actual bounding box independently in
   * BOTH the reference photo (for colour sampling) and whatever photo is
   * currently on screen (for final placement), and every pixel is mapped
   * through book-relative fractions rather than raw canvas position — see
   * mapMaskToOnscreen(). This keeps the overlay correctly placed even if a
   * future reshoot frames the book slightly differently again.
   * ------------------------------------------------------------------- */

  var REGIONS_REL = {
    // Measured directly against the actual reference photo with a careful
    // row-by-row raw-pixel read (not a statistical classifier, which kept
    // giving inconsistent answers against the linen's woven texture). The
    // band's dark-edge-then-ribbed-oscillation signature landed at the SAME
    // x[673-712] every time across the full height, out of a 1024px-wide
    // photo — against the CORRECTED book bbox below (not the contaminated
    // one an earlier version used), that's a straight vertical band.
    //
    // Both this and the loop entry below were originally calibrated against
    // a book bbox of x[14-878] y[364-1282] found by naively taking the
    // min/max of every non-white pixel in the photo. That bbox was wrong:
    // minX=14 came from the photo's drop shadow (median minX across rows
    // clear of the shadow is 205, confirmed by manually reading raw pixels —
    // rows y=400-1000 all start at x=205 with pure white before it), and
    // maxX=878 came from the pen loop's own protrusion (median maxX
    // excluding the loop's y-range is 824). findBookBBox() below now takes
    // the PER-ROW/COLUMN median instead of a global min/max specifically to
    // stop a small protrusion or the shadow from ever contaminating it like
    // this again. The true book bbox is x[205-824] y[368-1237] — every
    // number here is relative to THAT.
    // yMin/yMax run the full 0.0-1.0 height (touching both edges exactly) —
    // an elastic band on a hardcover journal wraps the whole cover top to
    // bottom; an earlier 0.01-0.97 margin left a visible gap at both ends.
    band: { xMin: 0.756, xMax: 0.819, yMin: 0.0, yMax: 1.0 },
    // The loop attaches right at the cover's true edge and protrudes
    // entirely past it (xMin starts at ~1.0, not before it — there's no
    // overlap onto the front cover itself, unlike an earlier version's
    // assumption). y[572-642] against book y[368-1237].
    loop: { xMin: 1.00, xMax: 1.09, yMin: 0.235, yMax: 0.315 }
  };

  var DAMPING = { band: 0.7, loop: 0.6 };

  function isNearWhite(r, g, b) {
    return r > 246 && g > 246 && b > 246;
  }

  function median(arr) {
    var sorted = arr.slice().sort(function (a, b) { return a - b; });
    return sorted[Math.floor(sorted.length * 0.5)];
  }

  /* Finds the book's bounding box using the MEDIAN of each row's left/right
     edge and each column's top/bottom edge, rather than a single global
     min/max of every non-white pixel. A naive global min/max is exactly
     what broke this twice before: the photo's drop shadow pulled minX far
     left (contaminating only a handful of rows near the bottom), and the
     pen loop's own protrusion pulled maxX far right (contaminating only the
     rows within the loop's height). Both are small, localized outliers —
     taking the median across all rows/columns instead means the vast
     majority of "normal" rows (plain cover, no accessory, no shadow) decide
     the answer, and a future reshoot with a bigger shadow or a wider loop
     won't silently reintroduce the same bug. */
  function findBookBBox(ctx, w, h) {
    var data = ctx.getImageData(0, 0, w, h).data;
    var step = 3;
    var rowMinXs = [], rowMaxXs = [], colMinYs = [], colMaxYs = [];

    for (var y = 0; y < h; y += step) {
      var rMin = -1, rMax = -1;
      for (var x = 0; x < w; x += step) {
        var i = (y * w + x) * 4;
        if (!isNearWhite(data[i], data[i + 1], data[i + 2])) {
          if (rMin === -1) rMin = x;
          rMax = x;
        }
      }
      if (rMin !== -1) { rowMinXs.push(rMin); rowMaxXs.push(rMax); }
    }

    for (var x2 = 0; x2 < w; x2 += step) {
      var cMin = -1, cMax = -1;
      for (var y2 = 0; y2 < h; y2 += step) {
        var i2 = (y2 * w + x2) * 4;
        if (!isNearWhite(data[i2], data[i2 + 1], data[i2 + 2])) {
          if (cMin === -1) cMin = y2;
          cMax = y2;
        }
      }
      if (cMin !== -1) { colMinYs.push(cMin); colMaxYs.push(cMax); }
    }

    if (!rowMinXs.length || !colMinYs.length) {
      // Fallback: treat the whole canvas as the "book" rather than produce a
      // degenerate zero-size box (e.g. an all-white or fully transparent photo).
      return { minX: 0, maxX: w, minY: 0, maxY: h };
    }

    return {
      minX: median(rowMinXs),
      maxX: median(rowMaxXs),
      minY: median(colMinYs),
      maxY: median(colMaxYs)
    };
  }

  /* Absolute pixel x-range for `target` at a given absolute y, on a given
     book bbox/canvas. Both band and loop are fixed rectangles relative to
     the book (see REGIONS_REL above) — using the SAME function for both
     destination and source rows in renderColoredOverlayDataUrl is what
     keeps them correctly positioned regardless of the reference vs.
     on-screen photo's own size. `absY` is accepted for interface symmetry
     with a per-row-varying range, even though neither target currently
     needs one. */
  function rowXRange(target, book, w, absY) {
    var bookW = book.maxX - book.minX;
    var rel = REGIONS_REL[target];
    return {
      xStart: Math.max(0, Math.round(book.minX + rel.xMin * bookW)),
      xEnd: Math.min(w, Math.round(book.minX + rel.xMax * bookW))
    };
  }

  function yRangeFromBook(target, book, h) {
    var bookH = book.maxY - book.minY;
    var rel = REGIONS_REL[target];
    return {
      yStart: Math.max(0, Math.round(book.minY + rel.yMin * bookH)),
      yEnd: Math.min(h, Math.round(book.minY + rel.yMax * bookH))
    };
  }

  function rgbToHsl(r, g, b) {
    r /= 255; g /= 255; b /= 255;
    var max = Math.max(r, g, b), min = Math.min(r, g, b);
    var h, s, l = (max + min) / 2;
    if (max === min) {
      h = s = 0;
    } else {
      var d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        default: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }
    return [h, s, l];
  }

  function hue2rgb(p, q, t) {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  }

  function hslToRgb(h, s, l) {
    var r, g, b;
    if (s === 0) {
      r = g = b = l;
    } else {
      var q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      var p = 2 * l - q;
      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }
    return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
  }

  function hexToRgb(hex) {
    var m = hex.replace('#', '');
    return [parseInt(m.substr(0, 2), 16), parseInt(m.substr(2, 2), 16), parseInt(m.substr(4, 2), 16)];
  }

  /* Prepares a target's source data from the reference (or fallback
     on-screen) photo: its book bbox, the pixel data needed to recolour it,
     and a baseline lightness for the damping formula (averaged across the
     target's actual per-row ranges, so it reflects only real band/loop
     pixels even though those ranges move row to row for the band). */
  function prepareRegionData(img, target) {
    var w = img.naturalWidth, h = img.naturalHeight;
    if (!w || !h) return null;

    var srcCanvas = document.createElement('canvas');
    srcCanvas.width = w;
    srcCanvas.height = h;
    var srcCtx = srcCanvas.getContext('2d');
    srcCtx.drawImage(img, 0, 0, w, h);

    var book = findBookBBox(srcCtx, w, h);
    var fullData = srcCtx.getImageData(0, 0, w, h).data;

    var yRange = yRangeFromBook(target, book, h);
    var sumL = 0, count = 0;
    for (var y = yRange.yStart; y < yRange.yEnd; y++) {
      var xr = rowXRange(target, book, w, y);
      for (var x = xr.xStart; x < xr.xEnd; x++) {
        var si = (y * w + x) * 4;
        sumL += rgbToHsl(fullData[si], fullData[si + 1], fullData[si + 2])[2];
        count++;
      }
    }

    return {
      target: target,
      fullW: w,
      fullH: h,
      book: book,
      regionData: fullData, // full-image data; row-range indices computed on demand
      baselineL: count ? sumL / count : 0.5
    };
  }

  /* Renders the recoloured overlay sized to the CURRENT on-screen photo's own
     pixel dimensions. Walks the destination row range at each y (following
     the band's diagonal via rowXRange, or the loop's fixed box), and for
     each row maps its x fraction to the SAME row-range computed against the
     classified (reference) photo's own book bbox — so both photos' rows are
     compared at the same relative position along the accessory regardless of
     the two photos' differing sizes/framing (see the big comment above
     REGIONS_REL for why that indirection is necessary at all). */
  function renderColoredOverlayDataUrl(info, hex, onscreenBook, onscreenW, onscreenH) {
    var target = info.target;
    var damping = DAMPING[target];
    var targetRgb = hexToRgb(hex);
    var targetHsl = rgbToHsl(targetRgb[0], targetRgb[1], targetRgb[2]);
    var targetH = targetHsl[0], targetS = targetHsl[1], targetL = targetHsl[2];

    var outCanvas = document.createElement('canvas');
    outCanvas.width = onscreenW;
    outCanvas.height = onscreenH;
    var outCtx = outCanvas.getContext('2d');
    var outData = outCtx.createImageData(onscreenW, onscreenH);
    var od = outData.data;

    var destYRange = yRangeFromBook(target, onscreenBook, onscreenH);
    var srcYRange = yRangeFromBook(target, info.book, info.fullH);
    var destYSpan = destYRange.yEnd - destYRange.yStart;
    var srcYSpan = srcYRange.yEnd - srcYRange.yStart;

    for (var destY = destYRange.yStart; destY < destYRange.yEnd; destY++) {
      var fy = destYSpan > 1 ? (destY - destYRange.yStart) / (destYSpan - 1) : 0;
      var srcY = Math.min(srcYRange.yEnd - 1, Math.round(srcYRange.yStart + fy * (srcYSpan - 1)));

      var destRow = rowXRange(target, onscreenBook, onscreenW, destY);
      var srcRow = rowXRange(target, info.book, info.fullW, srcY);
      var destRowW = destRow.xEnd - destRow.xStart;
      var srcRowW = srcRow.xEnd - srcRow.xStart;
      if (destRowW <= 0 || srcRowW <= 0) continue;

      for (var destX = destRow.xStart; destX < destRow.xEnd; destX++) {
        var fx = destRowW > 1 ? (destX - destRow.xStart) / (destRowW - 1) : 0;
        var srcX = Math.min(srcRow.xEnd - 1, Math.round(srcRow.xStart + fx * (srcRowW - 1)));

        var si = (srcY * info.fullW + srcX) * 4;
        var r = info.regionData[si], g = info.regionData[si + 1], b = info.regionData[si + 2];
        var origL = rgbToHsl(r, g, b)[2];

        var newL = targetL + (origL - info.baselineL) * damping;
        newL = Math.max(0, Math.min(1, newL));
        var newRgb = hslToRgb(targetH, targetS, newL);

        var di = (destY * onscreenW + destX) * 4;
        od[di] = newRgb[0]; od[di + 1] = newRgb[1]; od[di + 2] = newRgb[2]; od[di + 3] = 255;
      }
    }

    outCtx.putImageData(outData, 0, 0);
    return outCanvas.toDataURL('image/png');
  }

  var overlays = {};
  var regionInfo = {};
  var pendingColor = {};
  var preparing = {};
  var onscreenBookCache = null;
  var onscreenBookCacheKey = null;

  function getMainImage() {
    var candidates = document.querySelectorAll('.product-gallery__media img');
    for (var i = 0; i < candidates.length; i++) {
      var img = candidates[i];
      var media = img.closest('.product-gallery__media');
      if (media && !media.hasAttribute('hidden') && media.offsetParent !== null) {
        return img;
      }
    }
    return document.querySelector('.product-gallery__media img');
  }

  function loadImageForCanvas(src) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () { resolve(img); };
      img.onerror = reject;
      img.src = src;
    });
  }

  function ensureParentPositioned(img) {
    var parent = img.parentElement;
    var parentPosition = getComputedStyle(parent).position;
    if (parentPosition === 'static') {
      parent.style.position = 'relative';
    }
    return parent;
  }

  /* Finds the on-screen photo's own book bbox, cached per photo URL since it
     doesn't change while the same photo is displayed. Needed fresh (via a
     same-origin CORS-enabled reload, not the already-rendered <img>, which
     would taint the canvas) every time the on-screen photo actually changes. */
  function getOnscreenBook(onscreenSrc, onscreenImg) {
    if (onscreenBookCacheKey === onscreenSrc && onscreenBookCache) {
      return Promise.resolve(onscreenBookCache);
    }
    return loadImageForCanvas(onscreenSrc).then(function (loadedImg) {
      var w = loadedImg.naturalWidth, h = loadedImg.naturalHeight;
      var c = document.createElement('canvas');
      c.width = w; c.height = h;
      var ctx = c.getContext('2d');
      ctx.drawImage(loadedImg, 0, 0, w, h);
      var book = findBookBBox(ctx, w, h);
      onscreenBookCache = { book: book, w: w, h: h };
      onscreenBookCacheKey = onscreenSrc;
      return onscreenBookCache;
    });
  }

  function ensureOverlay(target) {
    if (overlays[target]) return overlays[target];

    var img = getMainImage();
    if (!img) return null;
    var parent = ensureParentPositioned(img);

    var el = document.createElement('div');
    el.className = 'lj-overlay';
    parent.appendChild(el);
    overlays[target] = el;

    if (!preparing[target]) {
      preparing[target] = true;
      var referenceImageUrl = root.getAttribute('data-reference-image');
      var srcUrl = referenceImageUrl || img.currentSrc || img.src;
      loadImageForCanvas(srcUrl).then(function (loadedImg) {
        regionInfo[target] = prepareRegionData(loadedImg, target);
        if (pendingColor[target]) {
          applyColor(target, pendingColor[target]);
        }
      }).catch(function (err) {
        console.error('BBB linen-journal-addons: could not prepare ' + target + ' region', err);
      });
    }

    return el;
  }

  function applyColor(target, hex) {
    var el = overlays[target];
    if (!el) return;
    var info = regionInfo[target];
    if (!info) return;

    var img = getMainImage();
    if (!img) return;
    var onscreenSrc = img.currentSrc || img.src;

    getOnscreenBook(onscreenSrc, img).then(function (onscreen) {
      var dataUrl = renderColoredOverlayDataUrl(info, hex, onscreen.book, onscreen.w, onscreen.h);
      el.style.backgroundImage = 'url(' + dataUrl + ')';
      if (lightboxOverlays[target]) {
        lightboxOverlays[target].style.backgroundImage = el.style.backgroundImage;
      }
    }).catch(function (err) {
      console.error('BBB linen-journal-addons: could not place ' + target + ' overlay', err);
    });
  }

  function setOverlayColor(target, hex) {
    var el = ensureOverlay(target);
    if (!el || !hex) return;
    pendingColor[target] = hex;
    if (regionInfo[target]) {
      applyColor(target, hex);
    }
  }

  function showOverlay(target, show) {
    var el = overlays[target];
    if (!el) return;
    el.classList.toggle('lj-overlay--visible', !!show);
    if (lightboxOverlays[target]) {
      lightboxOverlays[target].classList.toggle('lj-overlay--visible', !!show);
    }
  }

  /* ----------------------------------------------------------------------
   * Zoom / lightbox support — same approach as wool-journal-addons.js.
   * ------------------------------------------------------------------- */

  var lightboxOverlays = {};

  function syncLightboxOverlaySize(wrap) {
    var img = wrap.querySelector('.pswp__img');
    if (!img) return;
    Object.keys(lightboxOverlays).forEach(function (target) {
      lightboxOverlays[target].style.width = img.style.width;
      lightboxOverlays[target].style.height = img.style.height;
    });
  }

  function attachLightboxOverlays(pswpImg) {
    var wrap = pswpImg.parentElement;
    if (!wrap || wrap.querySelector('.lj-overlay')) return;

    Object.keys(overlays).forEach(function (target) {
      var source = overlays[target];
      var el = document.createElement('div');
      el.className = source.className;
      el.style.position = 'absolute';
      el.style.top = '0';
      el.style.left = '0';
      el.style.backgroundImage = source.style.backgroundImage;
      wrap.appendChild(el);
      lightboxOverlays[target] = el;
    });

    syncLightboxOverlaySize(wrap);

    var wrapObserver = new MutationObserver(function (mutations) {
      var relevant = mutations.some(function (m) {
        return m.target === wrap || (m.target.classList && m.target.classList.contains('pswp__img'));
      });
      if (relevant) syncLightboxOverlaySize(wrap);
    });
    wrapObserver.observe(wrap, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
  }

  function watchForLightboxImage(pswpEl) {
    var existingImg = pswpEl.querySelector('.pswp__img');
    if (existingImg) {
      attachLightboxOverlays(existingImg);
      return;
    }

    var innerObserver = new MutationObserver(function () {
      var img = pswpEl.querySelector('.pswp__img');
      if (img) {
        attachLightboxOverlays(img);
        innerObserver.disconnect();
      }
    });
    innerObserver.observe(pswpEl, { childList: true, subtree: true });
  }

  function watchForLightbox() {
    var bodyObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1 && node.classList && node.classList.contains('pswp')) {
            watchForLightboxImage(node);
          }
        });

        mutation.removedNodes.forEach(function (node) {
          if (node.nodeType === 1 && node.classList && node.classList.contains('pswp')) {
            lightboxOverlays = {};
          }
        });
      });
    });
    bodyObserver.observe(document.body, { childList: true });
  }

  function initOverlays() {
    root.querySelectorAll('[data-addon]').forEach(function (addonEl) {
      var target = addonEl.getAttribute('data-overlay-target');
      if (!target) return;

      var toggle = addonEl.querySelector('.checkbox-control input');
      var swatches = addonEl.querySelectorAll('input[type="radio"]');

      function currentColor() {
        var checked = addonEl.querySelector('input[type="radio"]:checked');
        var wrap = checked ? checked.closest('[data-color]') : addonEl.querySelector('[data-color]');
        return wrap ? wrap.getAttribute('data-color') : null;
      }

      setOverlayColor(target, currentColor());

      if (toggle) {
        toggle.addEventListener('change', function () {
          showOverlay(target, toggle.checked);
          updateDisplayedPrice();
        });
      }

      swatches.forEach(function (radio) {
        radio.addEventListener('change', function () {
          var wrap = radio.closest('[data-color]');
          if (wrap) setOverlayColor(target, wrap.getAttribute('data-color'));
        });
      });
    });
  }

  /* ----------------------------------------------------------------------
   * Live price update — identical approach to wool-journal-addons.js.
   * ------------------------------------------------------------------- */

  var priceObserver = null;

  function formatMoney(cents, format) {
    var amount = (cents / 100).toFixed(2);
    return format.replace('{{amount}}', amount);
  }

  function getAddonTotalCents() {
    var total = 0;
    root.querySelectorAll('[data-addon]').forEach(function (addonEl) {
      var toggle = addonEl.querySelector('.checkbox-control input');
      if (toggle && toggle.checked) {
        total += parseInt(addonEl.getAttribute('data-price'), 10) || 0;
      }
    });
    return total;
  }

  function getPriceElement() {
    return document.querySelector('price-list.price-list--product sale-price');
  }

  function updateDisplayedPrice() {
    var salePrice = getPriceElement();
    if (!salePrice) return;

    var baseCents = parseInt(root.getAttribute('data-base-price'), 10) || 0;
    var moneyFormat = root.getAttribute('data-money-format') || '${{amount}}';
    var totalText = formatMoney(baseCents + getAddonTotalCents(), moneyFormat);

    var textNodes = Array.prototype.filter.call(salePrice.childNodes, function (node) {
      return node.nodeType === 3;
    });
    var textNode = textNodes[textNodes.length - 1];

    if (priceObserver) priceObserver.disconnect();
    if (textNode) {
      textNode.textContent = totalText;
    } else {
      salePrice.appendChild(document.createTextNode(totalText));
    }
    if (priceObserver) priceObserver.observe(salePrice, { childList: true, characterData: true, subtree: true });
  }

  function watchNativePriceUpdates() {
    var salePrice = getPriceElement();
    if (!salePrice) return;

    priceObserver = new MutationObserver(function () {
      updateDisplayedPrice();
    });
    priceObserver.observe(salePrice, { childList: true, characterData: true, subtree: true });
  }

  /* ----------------------------------------------------------------------
   * Toggle -> reveal colour picker
   * ------------------------------------------------------------------- */

  root.querySelectorAll('[data-addon]').forEach(function (addonEl) {
    var toggle = addonEl.querySelector('.checkbox-control input');
    var colorWrap = addonEl.querySelector('.linen-journal-addon__colors');
    if (toggle && colorWrap) {
      toggle.addEventListener('change', function () {
        colorWrap.hidden = !toggle.checked;
      });
    }
  });

  /* ----------------------------------------------------------------------
   * Back-forward cache restore — identical approach to wool-journal-addons.js.
   * ------------------------------------------------------------------- */

  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;

    root.querySelectorAll('[data-addon]').forEach(function (addonEl) {
      var target = addonEl.getAttribute('data-overlay-target');
      if (!target) return;

      var toggle = addonEl.querySelector('.checkbox-control input');
      var checked = addonEl.querySelector('input[type="radio"]:checked');
      var wrap = checked ? checked.closest('[data-color]') : addonEl.querySelector('[data-color]');
      var hex = wrap ? wrap.getAttribute('data-color') : null;

      if (hex) setOverlayColor(target, hex);
      if (toggle) showOverlay(target, toggle.checked);
    });

    updateDisplayedPrice();
  });

  initOverlays();
  watchForLightbox();
  updateDisplayedPrice();
  watchNativePriceUpdates();
  hookAfterCartAdd();
  hookCart();
}());
